/**
 * KI-Coach auf dem Gerät: Zeilen sammeln (lokal entschlüsselt), rechnen lässt
 * `coachSummary`. Was hier herauskommt, zeigt die App vollständig an, bevor
 * es gesendet wird — und nur das geht hinaus.
 *
 * Bewusst nicht gelesen: Gedanken (`journal_notes`), Notizen am Tagebuchtag,
 * Fotos, Gewicht und Maße, Supplement-Namen. Was nicht gesammelt wird, kann
 * nicht versehentlich mitgehen.
 */

import { coachSummary } from '@kraftwerk/shared';
import type { CoachSummary, CoachWeeks } from '@kraftwerk/shared';
import { currentRole } from '../auth.js';
import { db } from './local.js';
import type { KraftwerkDatabase } from './local.js';
import { today } from './nutrition.js';
import { readProfile } from './profile.js';

export async function loadCoachSummary(
  weeks: CoachWeeks,
  now: Date = new Date(),
  database: KraftwerkDatabase = db,
): Promise<CoachSummary | null> {
  const profile = await readProfile(database);
  return coachSummary({
    today: today(profile.timezone, now),
    timeZone: profile.timezone,
    now,
    weeks,
    isChild: currentRole() === 'child',
    modules: profile.modules,
    sessions: await database.workout_sessions.toArray(),
    sets: await database.workout_sets.toArray(),
    exercises: await database.exercises.toArray(),
    slots: await database.training_slots.toArray(),
    trainingDays: await database.training_days.toArray(),
    food: await database.food_log.toArray(),
    targets: profile.targets
      ? { kcal: profile.targets.kcal, protein: profile.targets.protein }
      : null,
    water: await database.water_log.toArray(),
    waterTargetMl: profile.water.targetMl,
    intakes: await database.supplement_intakes.toArray(),
    // Nur die Skalen und Schlafstunden — `note`, Gewicht und Maße bleiben hier.
    journal: (await database.journal_entries.toArray()).map((row) => ({
      date: row.date,
      sleepHours: row.sleepHours,
      sleepQuality: row.sleepQuality,
      energy: row.energy,
      mood: row.mood,
      soreness: row.soreness,
      deletedAt: row.deletedAt,
    })),
  });
}
